import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { CloudActions, CostCenter } from '@portal/portal-core';
import { useAppDispatch } from 'store/hooks';
import { LayoutContext } from 'components/layout/layout-context';
import ActionToolbar from 'components/table/table-controls/action-toolbar';
import FilterSelectionToolbar from 'components/table/table-controls/filter-selection-toolbar';
import LicenseSearchControls from 'components/super-admin/license-search-controls';
import { useLicenseSearch } from 'components/super-admin/license-search-controls/use-license-search';
import { Button } from 'components/styled-primitives';

import { CloudAdminActions } from 'actions';
import { SuperAdminActions } from 'ducks/super-client';
import {
  convertArrayOfObjectsToCSV,
  downloadCSV,
  formatSuperAdminListForExport,
  getSuperAdminExportFilename,
} from 'utils';
import { MODAL_TYPES, TOOL_BUTTON_NAMES } from 'constants/super-admin';
import {
  selectCurrentLicenses,
  selectActiveLicenseRows,
  selectActiveFilters,
  selectIndividualLicenseSyncState,
} from 'selectors/super-admin';
import IndividualLicenseTable from './individual-license-table';

type LicenseTableRow = React.ComponentProps<typeof IndividualLicenseTable>['licenses'][number];

const omitFormatting = ['seatLimit', 'maxSharedComputers', 'costCenterName'];
// See institutional-license/index.tsx: keeps FjSortTable's hidden client-side pagination from
// truncating rows.
const UNLIMITED_TABLE_PAGE_SIZE = 1000;

interface FilterActionButtons {
  createLicenseButton: React.ReactElement;
  manageLicenseButton: React.ReactElement;
  exportButton: React.ReactElement;
  clearFilterButton: React.ReactElement;
  backToSearchButton: React.ReactElement;
}

const IndividualLicense = (): React.ReactElement => {
  // `selectCurrentLicenses` is built via `chainSelectors`, which returns `unknown`,
  // and it chains through untyped `.js` util selectors, so its rows are genuinely
  // `any` at this boundary (matching IndividualLicenseTable's own `any` treatment
  // of license rows). Cast the selector to a typed selector for `useSelector`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const licenses = useSelector(selectCurrentLicenses as (state: unknown) => any[]);
  const selectedRows = useSelector(selectActiveLicenseRows);
  const syncState = useSelector(selectIndividualLicenseSyncState);
  // `selectActiveFilters` reads a dynamic `.js` state slice and is typed `unknown`.
  const activeFilters = useSelector(selectActiveFilters) as Record<string, unknown>;
  const [dismissSyncPrompt, setDismissSyncPrompt] = useState(false);

  const dispatch = useAppDispatch();
  const showModal = (modalType: string, feature?: string): void => {
    dispatch(CloudAdminActions.showModal(modalType, feature));
  };
  const clearFilters = (): void => {
    dispatch(SuperAdminActions.clearFilters());
  };
  const getIndividualLicenses = (): void => {
    // `getIndividualLicensesVerbose(limit, requestId?)` takes an explicit limit;
    // pass `undefined` to preserve the prior no-arg (undefined limit) behavior.
    dispatch(CloudActions.getIndividualLicensesVerbose(undefined));
  };

  const dismissDataSync = (): void => {
    dispatch(SuperAdminActions.dismissLicenseSync());
    setDismissSyncPrompt(true);
  };
  const retryDataSync = (): void => {
    dispatch(CloudActions.getIndividualLicensesVerbose(undefined));
  };

  useEffect(() => {
    if (syncState.syncFailed) {
      setDismissSyncPrompt(false);
    }
  }, [syncState.syncFailed]);

  const { confirmDownload } = useContext(LayoutContext);

  const {
    mode,
    setMode,
    searchString,
    setSearchString,
    productNames,
    setProductNames,
    offset,
    setOffset,
    searchRows,
    searchTotal,
    searchLimit,
    searchLoading,
    searchError,
    searchHitCount,
  } = useLicenseSearch<LicenseTableRow>(
    TOOL_BUTTON_NAMES.INDIVIDUAL_LICENSE,
    CostCenter.COST_CENTER_TYPES.individual,
    getIndividualLicenses,
  );

  const exportList = (): void => {
    confirmDownload({
      filename: getSuperAdminExportFilename('super-admin-individual'),
      title: 'Export List?',
      ext: 'csv',
      accept: {
        text: 'Export',
        callback: (editedFilename: string) =>
          downloadCSV({
            filename: editedFilename,
            csvData: convertArrayOfObjectsToCSV({
              data: formatSuperAdminListForExport(licenses),
              doNotFormatTheseFields: omitFormatting,
              keys: [
                'primaryEmail',
                'firstname',
                'lastname',
                'ownerID',
                'region',
                'country',
                'costCenterID',
                'status',
                'activityState',
                'type',
                'product',
                'productAccessId',
                'seatLimit',
                'maxSharedComputers',
                'costCenterName',
                'whitelistDomains',
                'startDate',
                'endDate',
                'billingDate',
                'whitelistIgnore',
                'admins',
                'authorizedDevicesOverride',
                'features',
              ],
            }),
          }),
      },
    });
  };

  const filterActionButtons = (): FilterActionButtons => {
    const isManageLicenseDisabled = !Object.keys(selectedRows).length;
    const someFiltersAreActive = !!Object.values(activeFilters).filter(Boolean).length;

    return {
      createLicenseButton: (
        <Button key="create-license" onClick={() => showModal(MODAL_TYPES.CREATE_NEW_LICENSE)}>
          Create License
        </Button>
      ),
      manageLicenseButton: (
        <Button
          key="manage-license"
          onClick={() => showModal(MODAL_TYPES.MANAGE_LICENSE)}
          disabled={isManageLicenseDisabled}
        >
          Manage License
        </Button>
      ),
      exportButton: (
        <Button key="export" onClick={exportList}>
          Export List
        </Button>
      ),
      clearFilterButton: (
        <Button key="clear" disabled={!someFiltersAreActive} onClick={() => clearFilters()}>
          Clear Filter
        </Button>
      ),
      backToSearchButton: (
        <Button key="back-to-search" onClick={() => setMode('search')}>
          Back to search
        </Button>
      ),
    };
  };

  const renderActionToolbar = (): React.ReactElement => {
    const {
      createLicenseButton,
      manageLicenseButton,
      exportButton,
      clearFilterButton,
      backToSearchButton,
    } = filterActionButtons();

    // 'null' represents a spacer in the button layout. backToSearchButton is included here
    // (rather than rendered as a plain sibling Button next to ActionToolbar) so it gets the
    // same ButtonWrapper spacing and vertical centering every other action button gets — a
    // standalone sibling Button sat outside ActionToolbar's flex row and rendered top-aligned
    // instead of centered like Export List.
    const buttons: (React.ReactElement | null)[] = [
      createLicenseButton,
      manageLicenseButton,
      null,
      null,
      clearFilterButton,
      null,
      exportButton,
      null,
      backToSearchButton,
    ];

    // ActionToolbar consumes each element's `key`/`props`; React elements satisfy
    // that shape structurally, but their `key` is typed `string | null` (vs the
    // stricter `React.Key`), so cast to the toolbar's own `buttons` prop type.
    return (
      <ActionToolbar buttons={buttons as React.ComponentProps<typeof ActionToolbar>['buttons']} />
    );
  };

  // Create/Manage License work the same way regardless of which view produced the rows — row
  // selection (selectedRows) tracks by license id, not by data source. Export List and Clear
  // Filter are deliberately excluded: see institutional-license/index.tsx for the identical
  // rationale. (Individual licenses have no "Admin View" button in the legacy toolbar either, so
  // there's none to carry over here.)
  const renderSearchActionToolbar = (): React.ReactElement => {
    const { createLicenseButton, manageLicenseButton } = filterActionButtons();
    const buttons: (React.ReactElement | null)[] = [createLicenseButton, manageLicenseButton];
    return (
      <ActionToolbar buttons={buttons as React.ComponentProps<typeof ActionToolbar>['buttons']} />
    );
  };

  const isSearchMode = mode === 'search';
  const visibleLicenses = isSearchMode ? searchRows : licenses;

  return (
    <IndividualLicenseTable
      licenses={visibleLicenses}
      filterControls={
        isSearchMode ? (
          <LicenseSearchControls
            searchString={searchString}
            onSearchStringChange={setSearchString}
            productNames={productNames}
            onProductNamesChange={setProductNames}
            total={searchTotal}
            limit={searchLimit}
            offset={offset}
            onOffsetChange={setOffset}
            loading={searchLoading}
            onViewLegacy={() => setMode('legacy')}
            pageLicenseCount={searchRows.length}
            pageCostCenterCount={searchHitCount}
            error={searchError}
          />
        ) : (
          <FilterSelectionToolbar numberOfResults={licenses.length} />
        )
      }
      controls={isSearchMode ? renderSearchActionToolbar() : renderActionToolbar()}
      hidePagination={isSearchMode}
      defaultPageSize={isSearchMode ? UNLIMITED_TABLE_PAGE_SIZE : undefined}
      syncFailed={syncState.syncFailed && !dismissSyncPrompt}
      currentRequest={syncState.currentRequest}
      requestsCount={syncState.requestsCount}
      nextRequest={syncState.nextRequest}
      dismissed={dismissSyncPrompt}
      onRetrySync={retryDataSync}
      onDismissSyncPrompt={dismissDataSync}
    />
  );
};

export default IndividualLicense;
